import {
  type DateRange,
  type Range,
} from '@/components/listings/search/SearchFilters/shared/commonTypes'
import type { KeysMatchingOptional } from '@/helpers/types/KeysMatching'

import type { ListingFilterParams } from '../SearchFilters/types'
import { hasCategorySelection } from './categoryThreshold'
import { isDateRange, isRange, isStringList } from './typeInspectors'
import type { ListingFiltersState } from './types'

/*
Here be magic.

We want to ensure typescript catches a case where we have something in AuctionFiltersState
that is being ignored and not translated to an AuctionFilterParams value, and also that
we are translated from the correct AuctionFiltersState type to a valid AuctionFilterParams
type. For example...
1. state.storefront -> params.storefront
  - Is a string[] in state
  - Is a string[] in params
  - Mapping state.storefront directly to params.storefront should be ok
2. state.currentBidRanges -> params.<min/max>BidValue
  - Is a Range in state
  - Is represented by two separate `number` fields in params
  - Mapping from `state.currentBidRanges` to `params.<min/max>BidValue` should be ok,
    but null values and 0's need to be omitted
3. state.invalid -> params.storefront
  - Should fail with a type error, because invalid is not a valid key for state
4. state.storefront -> params.invalid
  - Should fail with a type error, because invalid is not a valid key for params
5. state.storefront -> params.q
  - Should fail with a type error, because storefront is string[] and q is string
6. state.q -> params.storefront
  - Should fail with a type error, because storefront is string[] and q is string
*/

type Translator<K extends keyof ListingFiltersState> = (
  formattedFilters: ListingFilterParams,
  key: K,
  value: ListingFiltersState[K]
) => void

type StringKeys = KeysMatchingOptional<ListingFiltersState, string> &
  KeysMatchingOptional<ListingFilterParams, string>
type StringListKeys = KeysMatchingOptional<ListingFiltersState, string[]> &
  KeysMatchingOptional<ListingFilterParams, string[]>
type BooleanKeys = KeysMatchingOptional<ListingFiltersState, boolean> &
  KeysMatchingOptional<ListingFilterParams, boolean>
type RangeStateKeys = KeysMatchingOptional<ListingFiltersState, Range>
type DateRangeStateKeys = KeysMatchingOptional<ListingFiltersState, DateRange>
type ParamsNumberKeys = KeysMatchingOptional<ListingFilterParams, number>

const booleanTranslation: Translator<BooleanKeys> = (
  formattedFilters,
  key,
  value
) => {
  if (value === true) {
    formattedFilters[key] = true
  }
}

const stringListTranslation: Translator<StringListKeys> = (
  formattedFilters,
  key,
  value
) => {
  if (isStringList(value) && value.length) {
    formattedFilters[key] = value
  }
}

const stringTranslation: Translator<StringKeys> = (
  formattedFilters,
  key,
  value
) => {
  if (typeof value === 'string' && value) {
    formattedFilters[key] = value
  }
}

const buildRangeTranslation =
  (
    lowerKey: ParamsNumberKeys,
    upperKey: ParamsNumberKeys
  ): Translator<RangeStateKeys> =>
  (formattedFilters, _key, value) => {
    if (isRange(value)) {
      if (value[0]) {
        formattedFilters[lowerKey] = value[0]
      }
      if (value[1]) {
        formattedFilters[upperKey] = value[1]
      }
    }
  }

// Narrow, literal-keyed rather than a KeysMatchingOptional<..., string>
// factory like buildRangeTranslation's — a generic string-keys union would
// also pick up narrow-literal string fields (e.g. distanceUnit: 'mi'|'km'),
// which breaks the indexed assignment below (TS requires the assigned
// value to satisfy every constituent of the key union, not just one).
const buildDateRangeTranslation =
  (
    lowerKey: 'minEndTime',
    upperKey: 'maxEndTime'
  ): Translator<DateRangeStateKeys> =>
  (formattedFilters, _key, value) => {
    if (isDateRange(value)) {
      if (value[0]) {
        formattedFilters[lowerKey] = value[0]
      }
      if (value[1]) {
        formattedFilters[upperKey] = value[1]
      }
    }
  }

const stateToParamsTranslators: {
  [K in keyof ListingFiltersState]: Translator<K>
} = {
  storefront: stringListTranslation,
  storeFrontId: stringListTranslation,
  category: stringListTranslation,
  categories: stringListTranslation,
  condition: stringListTranslation,
  inventoryType: stringListTranslation,
  productType: stringListTranslation,
  manufacturer: stringListTranslation,
  model: stringListTranslation,
  capacity: stringListTranslation,
  memory: stringListTranslation,
  carrier: stringListTranslation,
  sellerGrade: stringListTranslation,
  deals: stringListTranslation,
  starterLots: booleanTranslation,
  newSeller: booleanTranslation,
  isCreditCardEnabled: booleanTranslation,
  eligibleOnly: booleanTranslation,
  // myBids has no search-API equivalent — it's translated into the `id`
  // allowlist filter instead (see useMyBidsListingIdSync), so it's a
  // deliberate no-op here rather than an omission.
  myBids: () => {},
  // bidStatus has no search-API equivalent — it's translated into the `id`
  // allowlist filter via useWatchlistBidStatusSync (same rationale as myBids).
  bidStatus: () => {},
  // closesIn holds a bucket key ('5m' | '30m' | '1h' | '24h'); it's translated
  // into minEndTime/maxEndTime at request time in listingSearchStateToSearchParams
  // so `now` is re-evaluated on every fetch. This translator is a deliberate
  // no-op — the state field never maps directly to a search-API param.
  closesIn: () => {},
  listingType: stringListTranslation,
  buyingFormat: stringListTranslation,
  transportMode: stringListTranslation,
  allowedShipmentTypes: stringListTranslation,
  region: stringListTranslation,
  warehouseLocation: stringListTranslation,
  currentBidRanges: buildRangeTranslation('minBidValue', 'maxBidValue'),
  msrp: buildRangeTranslation('minRetailPrice', 'maxRetailPrice'),
  msrpPercentage: (formattedFilters, _key, value) => {
    // Omit MSRP query parameter if it's set to 100% (1)
    // Include it if it is 0 (even though that would result in no results)
    // Note: If set via the UI, `100%` will result in `null`. `1` will
    // only happen here via an explicit `msrpPercentage=1` query parameter
    if (typeof value === 'number' && value !== 1) {
      formattedFilters.maxPercentMsrp = Math.max(0, value)
    }
  },
  landedCostPercentage: (formattedFilters, _key, value) => {
    if (typeof value === 'number' && value !== 1) {
      formattedFilters.maxLandedCostPercentMsrp = Math.max(0, value)
    }
  },
  units: buildRangeTranslation('minUnits', 'maxUnits'),
  problemFreeOrderRate: (formattedFilters, _key, value) => {
    if (typeof value === 'number' && value > 0) {
      formattedFilters.minProblemFreeOrderRate = value / 100
    }
  },
  distanceRange: (formattedFilters, _key, value) => {
    if (value.coordinates && value.distance) {
      formattedFilters.distanceLong = value.coordinates.long
      formattedFilters.distanceLat = value.coordinates.lat
      formattedFilters.distanceRadius = value.distance
      formattedFilters.distanceUnit = 'mi'
    }
  },
  endTimeRange: buildDateRangeTranslation('minEndTime', 'maxEndTime'),
  q: stringTranslation,
  // Send categoryThreshold only when a category is selected.
  categoryThreshold: (formattedFilters, _key, value) => {
    if (typeof value === 'number' && hasCategorySelection(formattedFilters)) {
      formattedFilters.categoryThreshold = value
    }
  },
}

const formatFilters = (filters: ListingFiltersState) => {
  const formattedFilters: ListingFilterParams = {}
  Object.entries(filters).forEach(([key, value]) => {
    // This check should never fail, but just in case the `filters` object gets
    // extra properties attached to it unexpectedly, we want to gracefully ignore
    // them and not throw an error about there not being a translator for it
    if (key in stateToParamsTranslators) {
      // Sadly, we can't get Typescript to do the work of verifying this for us...
      // but since we're using Object.entries, and are in control of filters, we
      // can know that value will be of the type for `AuctionFiltersState[key]`.
      // Ideally, we'd use types that say this... but that can't be done so we
      // just pick one of the keys and pretend the type is that key to satisfy
      // typescript.
      // The typing of `AuctionFiltersState`, `AuctionFilterParams`, and
      // `stateToParamsTranslators` are the important parts doing the work
      // we care about w.r.t. type checking.
      const castedKey = key as 'storefront'
      const castedValue = value as ListingFiltersState[typeof castedKey]
      stateToParamsTranslators[castedKey](
        formattedFilters,
        castedKey,
        castedValue
      )
    }
  })

  return formattedFilters
}

export default formatFilters
